import {observable, action, computed} from 'mobx';
import {message} from '@enos/dpl';
import localeText, {getLocale} from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris/index';
import ShortcutApp from './models/shortcutApp';
import Menu from './models/Menu';
import Category from './models/Category';
import {transformVarToKey, transformKeyToVar} from '~/utils/tools';
import _ from 'lodash';

class ShortcutAppStore {
  @observable currentShortcutAppDetail = {};

  @observable currentShortcutAppId = '';

  @observable isLoading = false;

  @observable modifyLoading = false;

  @observable menus = [];

  @observable sortMenus = [];

  @observable menuUnAssignedList = [];

  @observable checkedKeys = [];

  @observable currentSourceAppId = ''; // 来源应用

  @observable categoryList = [];

  @observable permissionList = [];

  @observable permissions = [];

  @computed get currentShortcutAppName() {
    return transformVarToKey(this.currentShortcutAppDetail.nameJson);
  }

  @computed get currentShortcutAppDescription() {
    return transformVarToKey(this.currentShortcutAppDetail.descriptionJson);
  }

  @action setModifyLoading(boolean) {
    this.modifyLoading = boolean;
  }

  @action setLoading(boolean) {
    this.isLoading = boolean;
  }

  @action setCurrentShortcutAppId(id) {
    this.currentShortcutAppId = id;
  }

  @action setCurrentShortcutAppDetail(data) {
    this.currentShortcutAppDetail = data;
  }

  @action setMenus(data) {
    this.menus = data;
  }

  @action setSortMenus(data) {
    this.sortMenus = data;
  }

  @action setMenuUnAssignedList(data) {
    this.menuUnAssignedList = data;
  }

  @action setCurrentSourceAppId(id) {
    this.currentSourceAppId = id;
  }

  @action setCategoryList(data) {
    this.categoryList = data;
  }

  @action.bound
  getShortcut(appId) {
    this.setLoading(true);
    axiosInstance.get(uris.getShortcut(), {params: {appId}}).then((data) => {
      this.setCurrentShortcutAppDetail(this.updateShortcutAppFromServer(data));
      this.setLoading(false);
    });
  }

  @action.bound
  modifyShortcut(data) {
    this.setModifyLoading(true);
    return axiosInstance
      .post(uris.modifyShortcut(), {
        appId: this.currentShortcutAppId.trim(),
        nameJson: _.merge(transformKeyToVar(data.appName), {
          default: data.appName[getLocale()]
        }),
        descriptionJson: _.merge(transformKeyToVar(data.descriptionModify), {
          default: data.descriptionModify[getLocale()] || '-'
        }),
        categoryCode: data.categoryCodeModify
      })
      .then((json) => {
        this.updateShortcutAppFromServer(json);
        this.setModifyLoading(false);
        this.getShortcut(this.currentShortcutAppId.trim());
        message.success(localeText('APP.MESSAGE_MODIFY_SUCCEED'));
      })
      .catch(() => {
        this.setModifyLoading(false);
        return Promise.reject();
      });
  }

  @action.bound
  createAppShortcut(appId) {
    this.setLoading(true);
    return axiosInstance
      .post(
        uris.createAppShortcut(),
        {
          appId
        },
        {
          showError: {
            showPopup: true,
            title: localeText('APP.TITLE_ADD_SHORTCUT_FAIL')
          }
        }
      )
      .then(data => data)
      .catch(() => Promise.reject());
  }

  @action.bound
  getAppCategory() {
    axiosInstance
      .get(uris.getAppCategory())
      .then((data) => {
        this.setCategoryList(
          (data.categories || []).map(json => this.updateCategoryFromServer(json)
          )
        );
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  updateCategoryFromServer(json) {
    const category = new Category(this, json.id);
    category.updateFromJson(json);
    return category;
  }

  @action.bound
  updateShortcutAppFromServer(json) {
    const shortcutApp = new ShortcutApp(this, json.id);
    shortcutApp.updateFromJson(json);
    return shortcutApp;
  }

  @action.bound
  getMenu(appId) {
    this.setLoading(true);
    axiosInstance.get(uris.getMenu(), {params: {appId}}).then((data) => {
      this.setMenus(
        (data.menus || []).map(json => this.updateMenuFromServer(this.menus, json)
        )
      );
      this.setSortMenus(
        (data.menus || []).map(json => this.updateMenuFromServer(this.sortMenus, json)
        )
      );
      this.setLoading(false);
    });
  }

  @action.bound
  updateMenuFromServer(arr, json) {
    let menu = arr.find(({id}) => id === json.id);
    if (!menu) {
      menu = new Menu(this, json);
    }
    menu.updateFromJson(json);
    menu.updateChildren(
      (json.children || []).map(item => new Menu(this, item))
    );
    return menu;
  }

  @action.bound
  getShortcutAppMenuUnAssignedList(sourceAppId, appId) {
    this.setLoading(true);
    return axiosInstance
      .get(uris.getShortcutAppMenuUnAssignedList(), {
        params: {sourceAppId, appId}
      })
      .then((data) => {
        this.setMenuUnAssignedList(
          (data.menus || []).map(json => this.updateMenuFromServer(this.menuUnAssignedList, json)
          )
        );
        this.setLoading(false);
      });
  }

  @action.bound
  setCheckedKeys(checkedKeys) {
    this.checkedKeys = checkedKeys;
  }

  @action.bound
  shortcutAppAssign(parentId, appId, sourceAppId, menuIds) {
    this.setLoading(true);
    axiosInstance
      .post(uris.shortcutAppAssign(), {
        parentId,
        appId,
        sourceAppId,
        menuIds
      })
      .then(() => {
        message.success(localeText('APP.MSG_ADD_MENU_SUCCESS'));
        this.getMenu(this.currentShortcutAppId);
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  addFirstClassMenu(appId, name, url) {
    this.setLoading(true);
    return axiosInstance
      .post(uris.addFirstClassMenu(), {
        appId,
        nameJson: _.merge(transformKeyToVar(name), {
          default: name[getLocale()]
        }),
        url
      })
      .then(() => {
        message.success(localeText('APP.HINT_CREATE_MENU_SUCCEED'));
        this.getMenu(this.currentShortcutAppId);
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
        return Promise.reject();
      });
  }

  @action.bound
  deleteMenu(menuId) {
    this.setLoading(true);
    axiosInstance
      .post(uris.deleteMenu(), {
        menuId
      })
      .then(() => {
        message.success(localeText('APP.HINT_DELETE_MENU_SUCCEED'));
        this.getMenu(this.currentShortcutAppId);
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  checkMenuExist(name, id, locale, cb) {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'menu', name, id, locale},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error);
      });
  }

  @action.bound
  modifyMenu(menuId, name, url) {
    this.setLoading(true);
    axiosInstance
      .post(uris.modifyMenu(), {
        menuId,
        nameJson: _.merge(transformKeyToVar(name), {
          default: name[getLocale()]
        }),
        url
      })
      .then(() => {
        message.success(localeText('APP.MESSAGE_MODIFY_SUCCEED'));
        this.getMenu(this.currentShortcutAppId);
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  sortShortcutMenu(menus) {
    this.setLoading(true);
    axiosInstance
      .post(uris.sortShortcutMenu(), {
        menus
      })
      .then(() => {
        message.success(localeText('APP.HINT_SORT_MENU_SUCCESS'));
        this.getMenu(this.currentShortcutAppId);
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  getShortcutAppPer(appId) {
    this.setLoading(true);
    return axiosInstance
      .get(uris.getShortcutAppPer(), {params: {appId}})
      .then((data) => {
        this.setLoading(false);
        this.setShortcutAppPer(data.permissions);
        return data.permissions;
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  @action.bound
  setShortcutAppPer(data) {
    this.permissions = data;
  }

  @action.bound
  deletePer(permissionId) {
    axiosInstance
      .post(uris.deletePer(), {
        permissionId
      })
      .then(() => {
        this.getShortcutAppPer(this.currentShortcutAppId);
        message.success(localeText('APP.PRMTMSG_REMPERMISSION_SUCCESS'));
      });
  }

  @action.bound
  getAppPermissionList(sourceAppId, appId) {
    return axiosInstance
      .get(uris.getAppPermissionList(), {
        params: {sourceAppId, appId}
      })
      .then((data) => {
        this.setpermissionList(data.permissions);
        return data.permissions;
      });
  }

  @action.bound
  setpermissionList(json) {
    this.permissionList = json;
  }

  @action.bound
  assignPermission(appId, sourceAppId, permissionIds) {
    return axiosInstance
      .post(uris.assignShortcutPermission(), {
        appId,
        sourceAppId,
        permissionIds
      })
      .then(() => {
        message.success(localeText('APP.PRMTMSG_ASSPERMISSION_SUCCESS'));
      });
  }
}

export default new ShortcutAppStore();
